import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import * as PIXI from 'pixi.js'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { JACKPOT_LADDER_STEP_PRIZE_STYLE } from '@/containers/common/TextStyles'
import { toCurrency } from '@/ui/currency'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import Facade from '@/Facade'
import StateSignals from '@stateMachine/StateSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { isHoldAndWinInsideFreeSpins } from '@/states/freespins/FreeSpinsFinalWin'

type JackpotBoxKey = 'Grand' | 'major' | 'minor' | 'mini'

const JACKPOT_BOX_MULTIPLIERS: Record<JackpotBoxKey, number> = {
    Grand: 1000,
    major: 250,
    minor: 100,
    mini: 50,
}

const JACKPOT_BOX_FORMAT = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
})

const JACKPOT_BOX_VALUE_STYLE = {
    ...JACKPOT_LADDER_STEP_PRIZE_STYLE,
}

const JACKPOT_BOX_VALUE_STYLE_BY_LABEL: Record<JackpotBoxKey, typeof JACKPOT_BOX_VALUE_STYLE> = {
    Grand: {
        ...JACKPOT_BOX_VALUE_STYLE,
        fontSize: 17,
    },
    major: {
        ...JACKPOT_BOX_VALUE_STYLE,
        fontSize: 16,
    },
    minor: {
        ...JACKPOT_BOX_VALUE_STYLE,
        fontSize: 16,
    },
    mini: {
        ...JACKPOT_BOX_VALUE_STYLE,
        fontSize: 16,
    },
}

const JACKPOT_BOX_SPINE_SRC = 'jackpot_box'

const JACKPOT_BOX_LAYOUT = [
    {
        label: 'Grand',
        animation: 'Grand_idle',
        winAnimation: 'grand_win',
        x: 190,
        y: 335.5,
        textX: 190,
        textY: 305,
        portraitX: 68.5,
        portraitY: 488,
        portraitTextX: 71,
        portraitTextY: 458,
    },
    {
        label: 'major',
        animation: 'major_idle',
        winAnimation: 'major_win',
        x: 190,
        y: 542,
        textX: 190,
        textY: 515,
        portraitX: 1352,
        portraitY: 488,
        portraitTextX: 1357.5,
        portraitTextY: 459,
    },
    {
        label: 'minor',
        animation: 'minor_idle',
        winAnimation: 'minor_win',
        x: 190,
        y: 752,
        textX: 190,
        textY: 724,
        portraitX: 63.5,
        portraitY: 691,
        portraitTextX: 64,
        portraitTextY: 663,
    },
    {
        label: 'mini',
        animation: 'mini_idle',
        winAnimation: 'mini_win',
        x: 190,
        y: 958.5,
        textX: 190,
        textY: 930,
        portraitX: 1353.5,
        portraitY: 691,
        portraitTextX: 1358,
        portraitTextY: 663,
    },
] as const

const JACKPOT_BOX_SHOW_SIGNALS = [
    GameSignals.completeSplashScreenState,
    GameSignals.showBaseGameBackground,

    //## Paired with JACKPOT_BOX_SHOW_SIGNALS — the value text has its own list.
    GameSignals.previousRoundActive,
    (GameSignals as any).unfinishedRoundRequested,

    //## corner case, when the Hold & win feature after we got Max Exposure popup, basegame we need to show jackpot boxes.
    (GameSignals as any).maxExposureComplete,

    (GameSignals as any).holdAndWinStartSpin,
    (GameSignals as any).holdAndWinStop,
] as const

const JACKPOT_BOX_HIDE_SIGNALS = [
    GameSignals.bonusStartComplete,
    GameSignals.restoreFreeSpins,
    GameSignals.showFreeSpinsRetrigger,
    (GameSignals as any).holdAndWinTransitionStart,
] as const

let isHoldAndWinIntroPopupVisible = false
let isSplashScreenActive = true

// Tracks which jackpot boxes are currently looping their win animation. A box stays
// "won" from the moment its jackpot symbol lands until the collection phase begins,
// so the loop survives the intervening respins (each of which re-triggers a show).
const jackpotBoxWinActive: Record<JackpotBoxKey, boolean> = {
    Grand: false,
    major: false,
    minor: false,
    mini: false,
}

const resetJackpotBoxWinState = (): void => {
    ;(Object.keys(jackpotBoxWinActive) as JackpotBoxKey[]).forEach((key) => {
        jackpotBoxWinActive[key] = false
    })
}

const isRuntimeSplashState = (stateId?: string): boolean => {
    const current = String(stateId || (Facade as any).currentState || '')
    return (
        current === GameStatesEnum.GET_INIT_RESPONSE_STATE ||
        current === GameStatesEnum.BEFORE_LOADING_STATE ||
        current === GameStatesEnum.LOADING_STATE ||
        current === GameStatesEnum.INIT_STATE ||
        current === GameStatesEnum.DISPLAY_SPLASH_SCREEN_STATE
    )
}

const isHoldAndWinRespinEntered = (): boolean => {
    const currentState = String((Facade as any).currentState || '')
    return Boolean(Facade.model?.spinState?.isHoldAndWinActive) && currentState === GameStatesEnum.GET_SPIN_RESPONSE_STATE
}

const isHoldAndWinRuntimeActive = (): boolean => {
    return Boolean(Facade.model?.spinState?.isHoldAndWinActive)
}

const isFreeSpinsUiActive = (): boolean => {
    const model = Facade.model
    const currentState = String((Facade as any).currentState || '')

    //## During the Freespin, we got triggered Hold and win and Freespins 
    //## Here we need to show 2 popups, 1 - Freespin and 2 - Hold and win, 
    //## Continue the hold and win flow.
    if (!model?.roundState?.isFreeSpins && !model?.isBonusGameNow) {
        return false
    }

    const isFreeSpinsRound = Boolean(model?.roundState?.isFreeSpins || isHoldAndWinInsideFreeSpins(model))

    if (!isFreeSpinsRound) {
        return false
    }

    // Keep jackpots visible during activation pre-popup; hide once intro/retrigger (or later FS flow) is entered.
    return currentState !== GameStatesEnum.FREE_SPINS_ACTIVATE_STATE
}

const syncJackpotBoxContainer = (data: ICustomEventData): void => {
    const container = data.component.componentElement as PIXI.Container
    const currentState = String((Facade as any).currentState || '')
    const signalName = (data as any)?.signalName

    isSplashScreenActive = isRuntimeSplashState(currentState)

    if (signalName === (GameSignals as any).holdAndWinTransitionStart) {
        container.visible = false
        return
    }

    if (isSplashScreenActive) {
        container.visible = false
        return
    }

    //## Mirrors syncJackpotBoxContainerByState: activation must not change visibility either way.
    if (currentState === GameStatesEnum.HOLD_AND_WIN_ACTIVATE_STATE) {
        return
    }

    if (currentState === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE) {
        isHoldAndWinIntroPopupVisible = true
        container.visible = false
        return
    }

    if (isHoldAndWinRespinEntered()) {
        isHoldAndWinIntroPopupVisible = false
        container.visible = true
        return
    }

    if (currentState === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE || isHoldAndWinIntroPopupVisible) {
        container.visible = false
        return
    }

    if (isHoldAndWinRuntimeActive()) {
        container.visible = true
        return
    }

    if (isFreeSpinsUiActive()) {
        container.visible = false
        return
    }

    container.visible = true
}

const syncJackpotBoxContainerByState = (data: ICustomEventData): void => {
    const container = data.component.componentElement as PIXI.Container
    const stateId = String((data as any)?.signalData || '')

    isSplashScreenActive = isRuntimeSplashState(stateId)

    if (isSplashScreenActive) {
        container.visible = false
        return
    }

    //## Hold & Win activation corner case: the activation pre-popup is a state transition, but it must not change visibility either way.
    if (stateId === GameStatesEnum.HOLD_AND_WIN_ACTIVATE_STATE) {
        return
    }

    if (
        stateId === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE ||
        stateId === GameStatesEnum.FREE_SPINS_SUMMARY_STATE
    ) {
        if (stateId === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE) {
            isHoldAndWinIntroPopupVisible = true
        }
        container.visible = false
        return
    }

    if (stateId === GameStatesEnum.GET_SPIN_RESPONSE_STATE && Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
        isHoldAndWinIntroPopupVisible = false
        container.visible = true
        return
    }

    if (isHoldAndWinIntroPopupVisible) {
        container.visible = false
        return
    }

    if (isHoldAndWinRuntimeActive()) {
        container.visible = true
        return
    }

    container.visible = !isFreeSpinsUiActive()
}

const getCurrentTotalBet = (): number => {
    const totalBet = Number(Facade.model?.currentBaseBet ?? Facade.model?.currentBet ?? 0)
    return Number.isFinite(totalBet) && totalBet > 0 ? totalBet : 0
}

const getCurrencySymbol = (): string => {
    const rawCurrency = String(Facade.model?.initData?.player?.currency ?? '')
    return toCurrency(rawCurrency)
}

const formatJackpotValue = (multiplier: number): string => {
    const totalBet = getCurrentTotalBet()
    if (!Number.isFinite(totalBet) || totalBet <= 0 || !Number.isFinite(multiplier) || multiplier <= 0) {
        return ''
    }

    const majorUnits = toMajorUnits(Math.round(totalBet * multiplier))
    return `${getCurrencySymbol()}${JACKPOT_BOX_FORMAT.format(majorUnits)}`
}

const updateBoxValue = (data: ICustomEventData, boxKey: JackpotBoxKey, stateId?: string): void => {
    const element = data.component.componentElement as ScalingTextField

    isSplashScreenActive = isRuntimeSplashState(stateId)
    if (isSplashScreenActive) {
        element.visible = false
        return
    }

    if (isFreeSpinsUiActive() && !isHoldAndWinRuntimeActive()) {
        element.visible = false
        return
    }

    element.visible = true
    element.alpha = 1
    element.text = formatJackpotValue(JACKPOT_BOX_MULTIPLIERS[boxKey])
}

const syncBoxValueByState = (data: ICustomEventData, boxKey: JackpotBoxKey): void => {
    updateBoxValue(data, boxKey, String((data as any)?.signalData || ''))
}

const showBoxAnimation = (
    data: ICustomEventData,
    label: JackpotBoxKey,
    idleAnimation: string,
    winAnimation: string
): void => {
    // Block only while still in load/splash states — completeSplash may fire before the flag flips.
    if (isRuntimeSplashState()) {
        return
    }

    if (isFreeSpinsUiActive() && !isHoldAndWinRuntimeActive()) {
        hideBoxAnimation(data)
        return
    }

    const animation = data.component.componentElement as SpineAnimation
    const wasVisible = animation.visible
    animation.visible = true

    // A box that has already been won keeps looping its win animation across respins.
    // If it is already on-screen and looping the win, leave it running so the loop stays
    // continuous instead of restarting from the first frame on every respin.
    if (jackpotBoxWinActive[label]) {
        if (wasVisible) {
            return
        }

        try {
            animation.start([winAnimation], [true], 0, [1])
        } catch {
            // keep visible even if the animation state is temporarily unavailable
        }
        return
    }

    try {
        animation.start([idleAnimation], [true], 0, [1])
    } catch {
        // keep visible even if the animation state is temporarily unavailable
    }
}

const playBoxWinAnimation = (data: ICustomEventData, label: JackpotBoxKey, winAnimation: string): void => {
    jackpotBoxWinActive[label] = true

    const animation = data.component.componentElement as SpineAnimation
    animation.visible = true
    try {
        animation.start([winAnimation], [true], 0, [1])
    } catch {
        // ignore transient animation-state errors
    }
}

const stopBoxWinAnimation = (data: ICustomEventData, label: JackpotBoxKey, idleAnimation: string): void => {
    jackpotBoxWinActive[label] = false

    const animation = data.component.componentElement as SpineAnimation
    animation.visible = true
    try {
        animation.start([idleAnimation], [true], 0, [1])
    } catch {
        // ignore transient animation-state errors
    }
}

const hideBoxAnimation = (data: ICustomEventData): void => {
    resetJackpotBoxWinState()

    const animation = data.component.componentElement as SpineAnimation
    try {
        animation.stop()
    } catch {
        // ignore stop errors during state sync
    }

    animation.visible = false
}

const createBoxValueItem = (
    label: JackpotBoxKey,
    x: number,
    y: number,
    portraitX: number,
    portraitY: number
): IComponent => ({
    name: `Jackpot Box Value ${label}`,
    type: AssetType.CUSTOM,
    visible: false,
    position: [
        { orientation: Orientation.LANDSCAPE, x, y },
        { orientation: Orientation.PORTRAIT, x: portraitX, y: portraitY },
    ],
    elementFactory: () =>
        new ScalingTextField(0.9, 340, PIXI.BitmapText as any, JACKPOT_BOX_VALUE_STYLE_BY_LABEL[label]),
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                GameSignals.completeSplashScreenState,
                GameSignals.showBaseGameBackground,
                
                //## Paired with JACKPOT_BOX_SHOW_SIGNALS — the value text has its own list.
                GameSignals.previousRoundActive,
                (GameSignals as any).unfinishedRoundRequested,
                (GameSignals as any).maxExposureComplete,

                GameSignals.betHasChanged,
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStartSpin,
                (GameSignals as any).holdAndWinStop,
                (GameSignals as any).featureIntroRequested,
                (GameSignals as any).featureIntroDisplayRequested,
                (GameSignals as any).featureIntroContinue,
            ],
            callback: (data: ICustomEventData) => updateBoxValue(data, label),
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [StateSignals.stateChanged as any],
            callback: (data: ICustomEventData) => syncBoxValueByState(data, label),
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                GameSignals.bonusStartComplete,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
                GameSignals.showFreeSpinsSummary,
            ],
            callback: null,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
            callback: null,
        },
    ],
})

const createBoxSpineItem = (
    label: JackpotBoxKey,
    animation: string,
    winAnimation: string,
    x: number,
    y: number,
    portraitX: number,
    portraitY: number
): IComponent => ({
    name: `Jackpot Box ${label}`,
    type: AssetType.SPINE,
    src: JACKPOT_BOX_SPINE_SRC,
    visible: false,
    animationSettings: {
        default: {
            triggerGameSignals: [],
            animNames: [animation],
            loops: [true],
            track: 0,
            timeScales: [1],
        },
    },
    position: [
        { orientation: Orientation.LANDSCAPE, x, y },
        { orientation: Orientation.PORTRAIT, x: portraitX, y: portraitY },
    ],
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: JACKPOT_BOX_SHOW_SIGNALS as any,
            callback: (data: ICustomEventData) => showBoxAnimation(data, label, animation, winAnimation),
        },
        {
            // Matching jackpot symbol landed on the H&W board: loop this box's win animation.
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinJackpotSymbolLanded],
            callback: (data: ICustomEventData) => {
                const landedTier = String((data as any)?.signalData || '').toLowerCase()
                if (landedTier === label.toLowerCase()) {
                    playBoxWinAnimation(data, label, winAnimation)
                }
            },
        },
        {
            // Collection phase started (or H&W ended): stop the win loop, return to idle.
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                (GameSignals as any).holdAndWinCollectStarted,
                (GameSignals as any).holdAndWinStop,
            ],
            callback: (data: ICustomEventData) => stopBoxWinAnimation(data, label, animation),
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: JACKPOT_BOX_HIDE_SIGNALS as any,
            callback: hideBoxAnimation,
        },
    ],
})

export const JACKPOT_BOX_COMPONENT: IComponent = {
    name: ContainersNames.JACKPOT_BOX_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                (GameSignals as any).displaySplashScreenState,
                GameSignals.completeSplashScreenState,
                GameSignals.showBaseGameBackground,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
                GameSignals.bonusStartComplete,
                GameSignals.showFreeSpinsSummary,
                (GameSignals as any).featureIntroRequested,
                (GameSignals as any).featureIntroDisplayRequested,
                (GameSignals as any).featureIntroContinue,
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStartSpin,
                (GameSignals as any).holdAndWinStop,
                //## The unfinished-round popup 
                (GameSignals as any).unfinishedRoundRequested,
                (GameSignals as any).maxExposureComplete,
            ],
            callback: syncJackpotBoxContainer,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [StateSignals.stateChanged as any],
            callback: syncJackpotBoxContainerByState,
        },
    ],
    position: [
        { orientation: Orientation.LANDSCAPE, x: 114, y: 138.5 },
        { orientation: Orientation.PORTRAIT, x: 114, y: 138.5 },
    ],
    scale: [
        { orientation: Orientation.LANDSCAPE, x: 0.6, y: 0.6 },
        { orientation: Orientation.PORTRAIT, x: 0.6, y: 0.6 },
    ],
    items: [
        ...JACKPOT_BOX_LAYOUT.map(({ label, animation, winAnimation, x, y, portraitX, portraitY }) =>
            createBoxSpineItem(label, animation, winAnimation, x, y, portraitX, portraitY)
        ),
        ...JACKPOT_BOX_LAYOUT.map(({ label, textX, textY, portraitTextX, portraitTextY }) =>
            createBoxValueItem(label, textX, textY, portraitTextX, portraitTextY)
        ),
    ],
}
